export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatSource {
  title: string;
  url?: string;
  snippet: string;
}

export interface ChatStreamHandlers {
  onDelta: (text: string) => void;
  onDone: (sources: ChatSource[]) => void;
}

export interface WidgetConfig {
  id: string;
  name: string;
  theme: {
    primaryColor: string;
    fontFamily: string;
  };
  placeholder: string;
  welcomeMessage: string;
}

export class ApiClient {
  private baseUrl: string;
  private widgetId: string;

  constructor(baseUrl: string, widgetId: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.widgetId = widgetId;
  }

  async getConfig(): Promise<WidgetConfig> {
    const response = await fetch(
      `${this.baseUrl}/api/widget/config?widgetId=${encodeURIComponent(this.widgetId)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Sends a message and streams the reply. The backend responds with
   * newline-delimited JSON events: `{type:"delta",text}` per chunk and a
   * terminal `{type:"done",sources}` (or `{type:"error",message}`). Calls the
   * handlers as events arrive; resolves once the stream completes.
   */
  async sendMessage(
    message: string,
    conversationHistory: ChatMessage[],
    handlers: ChatStreamHandlers
  ): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        conversationHistory,
        widgetId: this.widgetId,
      }),
    });

    if (!response.ok || !response.body) {
      throw new Error(`Failed to send message: ${response.statusText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const handleLine = (line: string): void => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const event = JSON.parse(trimmed);
      if (event.type === 'delta') {
        handlers.onDelta(event.text);
      } else if (event.type === 'done') {
        handlers.onDone(event.sources ?? []);
      } else if (event.type === 'error') {
        throw new Error(event.message ?? 'Streaming error');
      }
    };

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newline: number;
      while ((newline = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        handleLine(line);
      }
    }
    // Flush any trailing line without a final newline.
    if (buffer.trim()) handleLine(buffer);
  }
}
